import type { ICluster, IRegion } from "@agrigate/location-types";
import useAsyncTask from "@agrigate/shared/lib/hooks/use-async-task";
import { ensureNN } from "@agrigate/ts-utils/lib/coercions";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import type { Combobox, ComboboxRootProps } from "@chakra-ui/react";
import { compact } from "lodash";
import { useEffect, useMemo } from "react";
import ComboboxSelect from "./ComboSelect";
import { getRPC } from "./utils/rpc";

export interface Cluster {
    clusterId: string;
    clusterName: string;
}

export default function ClusterDropDown(p: {
    name?: string;
    regionId?: MaybeN<string>;
    districtId?: MaybeN<string>;
    blockId?: MaybeN<string>;
    clusterId?: MaybeN<string>;
    defaultValue?: MaybeN<string>;
    defaultValues?: MaybeN<string[]>;
    onRegionInit?: (region: IRegion) => void;
    onChange?: (
        clusterId?: string,
        clusterName?: string,
        regionId?: string,
        districtId?: string,
    ) => void;
    autofetchInitial?: MaybeN<boolean>;
    disabled?: boolean;
    width?: string;
    allowCreation?: boolean;
    placement?: ComboboxRootProps["placeContent"];
    allowFetchAllClusters?: boolean;
    omitClusterIds?: MaybeN<string[]>;
    setDefaultValues?: (clusters: Cluster[]) => void;
    triggerFetchAll?: boolean;
    portalled?: boolean;
    isDesktop?: boolean;
    comboBoxProps?: Omit<Combobox.ControlProps, "children">;
    comboInputProps?: Omit<Combobox.InputProps, "children">;
    comboRootProps?: Omit<
        Combobox.RootProps,
        | "children"
        | "collection"
        | "onInputValueChange"
        | "value"
        | "onValueChange"
    >;
}) {
    const fetchClustersT = useAsyncTask(
        async () => {
            if (!p.regionId && !p.allowFetchAllClusters) return;
            return getRPC().location.findAllClusters({
                filters: {
                    regionId: { eq: p.regionId },
                    districtId: { eq: p.districtId },
                    blockId: { eq: p.blockId },
                },
            });
        },
        {
            deps: [p.regionId, p.districtId, p.disabled],
            autoRun: true,
        },
    );

    const clusters: MaybeN<ICluster[]> = fetchClustersT.data?.clusters;

    const clusterOptions = useMemo(() => {
        if (!clusters) return [];
        const options: {
            value: string;
            label: string;
            item: MaybeN<ICluster>;
        }[] = clusters
            .filter(cluster => !p.defaultValues?.includes(cluster.id))
            .map(cluster => ({
                value: cluster.id,
                label: p.isDesktop ? cluster.name : cluster.name.slice(0, 30),
                item: cluster,
            }));
        if (!p.allowCreation || !p.regionId) return options;
        return options.concat({
            value: "$new",
            label: "New Cluster",
            item: null,
        });
    }, [clusters, p.allowCreation, p.defaultValues]);

    useEffect(() => {
        if (p.triggerFetchAll && clusters) {
            p.setDefaultValues?.(
                clusters.map(cluster => ({
                    clusterId: cluster.id,
                    clusterName: cluster.name,
                })),
            );
        }
    }, [p.triggerFetchAll]);

    useEffect(() => {
        if (p.defaultValues && clusters?.length) {
            const selectedClusters = p.defaultValues.map(clusterId => {
                const cluster = clusters?.find(_ => _.id === clusterId);
                if (cluster)
                    return {
                        clusterId: cluster?.id,
                        clusterName: cluster?.name,
                    };
                else return null;
            });
            p.setDefaultValues?.(compact(selectedClusters));
        }
    }, [clusters]);

    return (
        <ComboboxSelect
            placeholder="Select Cluster"
            // Handle disabled state via root props
            comboRootProps={{
                disabled:
                    p.disabled || (!p.regionId && !p.allowFetchAllClusters),
                ...p.comboRootProps,
            }}
            comboBoxProps={{ ...p.comboBoxProps }}
            comboInputProps={{ ...p.comboInputProps }}
            // ComboboxSelect expects string[], so we wrap the single ID
            value={p.clusterId ? [p.clusterId] : []}
            // Map clusterOptions to the required ComboboxItem interface
            items={
                clusterOptions?.map(opt => ({
                    label: opt.label,
                    value: opt.value,
                })) ?? []
            }
            onChange={async selectedIds => {
                const selectedId = selectedIds[0];
                let cluster: MaybeN<ICluster>;

                if (selectedId === "$new") {
                    const name = prompt("Enter cluster name")?.trim();
                    if (!name) return;

                    ({ cluster } = await getRPC().location.addCluster({
                        name,
                        regionId: ensureNN(p.regionId),
                        districtId: p.districtId,
                    }));
                } else {
                    cluster = selectedId
                        ? clusters?.find(c => c.id === selectedId)
                        : null;
                }

                p.onChange?.(
                    cluster?.id,
                    cluster?.name,
                    cluster?.regionId,
                    cluster?.districtId ?? undefined,
                );
                fetchClustersT.trigger();
            }}
            width={p.width ?? "20%"}
            placement={p.placement}
            portalled={p.portalled ?? true}
        />
    );
}
